import axios, { AxiosInstance } from 'axios';
import { useCallback } from 'react';
import { HttpResponseStatus } from './common.model';
import {
  ENV_CONFIG_API_BMS_BASE_URL,
  ENV_CONFIG_API_BMS_ECS_BASE_URL,
  ENV_CONFIG_API_PM_BASE_URL,
} from './constants/env';

const setAxiosReqInterceptor = (
  instance: AxiosInstance,
  accessToken: string,
) => {
  return instance.interceptors.request.use((config) => {
    if (accessToken && config.headers) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }
    return config;
  });
};

const setAxiosResInterceptor = (instance: AxiosInstance) => {
  return instance.interceptors.response.use(
    (response) => {
      if (
        response.data._status &&
        response.data._status !== HttpResponseStatus.OK
      ) {
        return Promise.reject(response.data._message);
      }
      return response;
    },
    (error) => {
      return Promise.reject(error);
    },
  );
};

const setPmAxiosResInterceptor = (instance: AxiosInstance) => {
  return instance.interceptors.response.use(
    (response) => {
      if (
        response.data.error &&
        response.data.error.status_code &&
        response.data.error.status_code !== HttpResponseStatus.OK
      ) {
        return Promise.reject(response.data.error);
      }
      return response;
    },
    (error) => {
      if (error?.data?.error) {
        return error;
      } else {
        return Promise.reject(error);
      }
    },
  );
};

const ejectAxiosReqInterceptor = (instance: number) => {
  if (instance) axios.interceptors.request.eject(instance);
};

const ejectAxiosResInterceptor = (instance: number) => {
  if (instance) axios.interceptors.response.eject(instance);
};

const pmAxios = axios.create({
  baseURL: ENV_CONFIG_API_PM_BASE_URL,
});

const bmsAxios = axios.create({
  baseURL: ENV_CONFIG_API_BMS_BASE_URL,
});

const bmsEcsAxios = axios.create({
  baseURL: ENV_CONFIG_API_BMS_ECS_BASE_URL,
});

const allAxiosInstances = [pmAxios, bmsAxios, bmsEcsAxios];

const useAxiosRequestInterceptor = () => {
  const setInterceptor = useCallback(
    (accessToken: string) =>
      allAxiosInstances.map((instance) =>
        setAxiosReqInterceptor(instance, accessToken),
      ),
    [],
  );

  const ejectInterceptor = useCallback(
    (interceptorIds: number[]) =>
      interceptorIds.forEach((instance) => ejectAxiosReqInterceptor(instance)),
    [],
  );

  return {
    setInterceptor,
    ejectInterceptor,
  };
};

const useAxiosResponseInterceptor = () => {
  const setInterceptor = useCallback(
    () => allAxiosInstances.map((instance) => setAxiosResInterceptor(instance)),
    [],
  );

  const ejectInterceptor = useCallback(
    (interceptorIds: number[]) =>
      interceptorIds.forEach((instance) => ejectAxiosResInterceptor(instance)),
    [],
  );

  return {
    setInterceptor,
    ejectInterceptor,
  };
};

const usePmAxiosResponseInterceptor = () => {
  const setInterceptor = useCallback(
    () =>
      allAxiosInstances.map((instance) => setPmAxiosResInterceptor(instance)),
    [],
  );

  const ejectInterceptor = useCallback(
    (interceptorIds: number[]) =>
      interceptorIds.forEach((instance) => ejectAxiosResInterceptor(instance)),
    [],
  );

  return {
    setInterceptor,
    ejectInterceptor,
  };
};

const setUserOrgIdHeader = (orgId: string) => {
  pmAxios.defaults.headers.common['x-org-id'] = orgId;
  bmsAxios.defaults.headers.common['x-org-id'] = orgId;
  bmsEcsAxios.defaults.headers.common['x-org-id'] = orgId;
};

export {
  bmsAxios,
  bmsEcsAxios,
  pmAxios,
  setUserOrgIdHeader,
  useAxiosRequestInterceptor,
  useAxiosResponseInterceptor,
  usePmAxiosResponseInterceptor,
};
